import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import debounce from 'lodash-es/debounce'
import React, { useCallback, useEffect, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { resetPassword } from '../../globalHeader/functions/ams-functions'
import BaseDialog from '../Common/BaseDialog'
import ErrorBlock from '../Common/ErrorBlock'
import HidePasswordIcon from '../Icons/hidePassword'
import ShowPasswordIcon from '../Icons/showPassword'
import SignIn from './SignIn'
import { validatePassword } from '../../utils/passwordValidation'

export interface IResetPasswordProps {
    newPasswordToken: string
}

const ResetPassword: React.FC<IResetPasswordProps> = (props) => {
    const { newPasswordToken } = props

    const [isPasswordValid, setIsPasswordValid] = useState<boolean | undefined>(undefined)
    const [doPasswordsMatch, setDoPasswordsMatch] = useState<boolean | undefined>(undefined)
    const [password, setPassword] = useState('')
    const [confirmationPassword, setConfirmationPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmationPassword, setShowConfirmationPassword] = useState(false)
    const [isFormValid, setIsFormValid] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('')
    const { registerDialog, unregisterDialog } = useDialogs()

    useEffect(() => {
        if (isPasswordValid && doPasswordsMatch) {
            setIsFormValid(true)
        } else {
            setIsFormValid(false)
        }
    }, [isPasswordValid, doPasswordsMatch])

    // -------------------------------------------------------------- Password validation -----------------

    const onPasswordChange = (password: string) => {
        const result = validatePassword(password)
        setIsPasswordValid(result.isValid)
        setPasswordErrorMessage(result.errorMessage)
    }

    const debouncedPasswordChange = useCallback(debounce(onPasswordChange, 1000), [])

    useEffect(() => {
        if (password === '') {
            setIsPasswordValid(undefined)
            setPasswordErrorMessage('')
            return
        }

        const timer = setTimeout(() => {
            debouncedPasswordChange(password)
        }, 1000)

        return () => {
            clearTimeout(timer)
        }
    }, [password, debouncedPasswordChange])

    // -------------------------------------------------------------- Confirm password validation -----------------

    const onConfirmationPasswordChange = (confirmationPassword: string, password: string) => {
        setDoPasswordsMatch(confirmationPassword === password)
    }

    const debouncedConfirmationPasswordChange = useCallback(debounce(onConfirmationPasswordChange, 500), [])

    useEffect(() => {
        if (confirmationPassword === '' || password === '') {
            setDoPasswordsMatch(undefined)
            return
        }

        const timer = setTimeout(() => {
            debouncedConfirmationPasswordChange(confirmationPassword, password)
        }, 500)

        return () => {
            clearTimeout(timer)
        }
    }, [confirmationPassword, password, debouncedConfirmationPasswordChange])

    const submitHandler = async () => {
        if (isPasswordValid) {
            const result = await resetPassword(newPasswordToken, password)
            if (!result.error) {
                // TODO: dialog instead of alert
                alert('Your password has been successfully reset.')
                closeHandler()
            } else {
                setErrorMessage(result.error.message)
            }
        }
    }

    const closeHandler = () => {
        unregisterDialog(Dialogs.RESET_PASSWORD)
        registerDialog(Dialogs.SIGNIN, <SignIn />)
    }

    return (
        <BaseDialog name={Dialogs.RESET_PASSWORD} title={'Set up your new password'} onClose={closeHandler} forceBlock>
            {errorMessage && <ErrorBlock error={errorMessage} />}
            <TextField
                label="Enter your password:"
                margin="normal"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                            <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                error={!isPasswordValid && isPasswordValid !== undefined}
                helperText={
                    !isPasswordValid && isPasswordValid !== undefined && passwordErrorMessage
                }
            />
            <TextField
                label="Confirm your password:"
                margin="normal"
                type={showConfirmationPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={confirmationPassword}
                onChange={(e) => setConfirmationPassword(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                            <IconButton
                                edge="end"
                                onClick={() => setShowConfirmationPassword(!showConfirmationPassword)}
                            >
                                {showConfirmationPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                error={!doPasswordsMatch && doPasswordsMatch !== undefined}
                helperText={!doPasswordsMatch && doPasswordsMatch !== undefined && 'Passwords do not match'}
            />
            <Button size="large" fullWidth onClick={submitHandler} disabled={!isFormValid}>
                Reset
            </Button>
        </BaseDialog>
    )
}

export default ResetPassword
